/** A decision an event answer moved: shown as the card's doing, not the player's (screens §P6 Decisions; the card's rationale covers it). */
import type { DraftView, DecisionCatalogEntry, TeamState } from "@bsim/contracts";
import type { PreviewTrace } from "@bsim/domain";
import { changeTag, optionLabel, readDecision, subOf, type SettingValue } from "./decisionValues";

/** The title of the event card a constraint's `sourceRef` ("EV-02:C") names, from the team's pending cards; "an event card" once the card is gone. */
export const cardTitleOf = (team: TeamState, sourceRef: string | undefined): string => {
  const eventId = (sourceRef ?? "").split(":")[0];
  return team.pendingEvents.find((e) => e.card.eventId === eventId)?.card.title ?? "an event card";
};

/** The writer of a decision this round when it was not the player (ADR-0053 `DraftView.provenance`: the last writer of each path that moved), else null. A decision the player also wrote is theirs. */
function nonPlayerWriter(entry: DecisionCatalogEntry, draft: DraftView): NonNullable<DraftView["provenance"]>[number] | null {
  const rows = (draft.provenance ?? []).filter((p) => subOf(p.decisionPath, entry.decisionId) !== null);
  return rows.length === 0 || rows.some((p) => p.source === "player") ? null : rows[0] ?? null;
}

/** `"Deposit run" · Hold the line` for the card answer a constraint's `sourceRef` ("EV-02:C") names. */
const cardAnswerText = (team: TeamState, ref: string | undefined): string => {
  const [eventId, choiceId] = (ref ?? "").split(":");
  const choice = team.pendingEvents.find((e) => e.card.eventId === eventId)?.card.choices.find((c) => c.id === choiceId)?.label;
  return `"${cardTitleOf(team, ref)}"${choice === undefined ? "" : ` · ${choice}`}`;
};

/**
 * The desk row of a decision whose card hold ended at the round's open (the provenance row of source "policy", `by` "card-expired:EV-02:C"): where it is now and why. The setting is whatever the open put back, the team's own
 * from before the card or the standing policy; the card is not named (it is last year's, no longer among the pending cards).
 */
const backLine = (entry: DecisionCatalogEntry, draft: DraftView, path: string): string => {
  const sub = subOf(path, entry.decisionId), options = sub === null ? undefined : entry.options[sub];
  const value = sub === null ? undefined : readDecision(draft.decisions, entry.decisionId, sub);
  return options === undefined || value === undefined ? "Set by your standing policy: the card\u2019s hold ended and the setting went back" : `Back to ${optionLabel(options, value)}: the hold from your card answer ended with last year`;
};

/**
 * Who set a decision that differs from where the round opened, when it was not the player, and the line that says so: a card's answer (named, with the way to undo it), the standing
 * policy (a card's hold ended and the setting went back: no author) or the regulator. Read from `DraftView.provenance`; a draft without it falls back to the card constraints.
 */
export function setBy(entry: DecisionCatalogEntry, draft: DraftView, team: TeamState): { readonly source: "card" | "policy" | "regulator"; readonly text: string } | null {
  if (draft.provenance !== undefined) {
    const w = nonPlayerWriter(entry, draft);
    if (w === null) return null;
    if (w.source === "card") return { source: "card", text: `Set by your card answer: ${cardAnswerText(team, w.by?.[0])}` };
    if (w.source === "policy") return { source: "policy", text: backLine(entry, draft, w.decisionPath) };
    if (w.source === "regulator") return { source: "regulator", text: "Set by your regulator" };
    return null;
  }
  const row = draft.activeConstraints.find((c) => c.source === "event" && subOf(c.decisionId, entry.decisionId) !== null);
  if (row === undefined || changeTag(entry, draft.decisions, draft.seedDecisions) === null) return null;
  return { source: "card", text: (row.sourceRef ?? "").includes(":") ? `Set by your card answer: ${cardAnswerText(team, row.sourceRef)}` : "Set by your card answer" };
}

/** The `setBy` line for a decision a card, the policy or the regulator set; null for the player's own move. */
export const forcedLine = (entry: DecisionCatalogEntry, draft: DraftView, team: TeamState): string | null => setBy(entry, draft, team)?.text ?? null;

const SOURCE_WORDS = { mou: "your regulator", consentOrder: "your regulator", pca: "your regulator", dealCovenant: "the deal covenant", activist: "the activist agreement" } as const;

/** Who set a decision this round when it was not the player: "your answer to "Deposit run"", "your regulator", "the activist agreement"; null for the player's own move. */
export function decisionSource(entry: DecisionCatalogEntry, draft: DraftView, team: TeamState): string | null {
  if (draft.provenance !== undefined) {
    const w = nonPlayerWriter(entry, draft);
    return w === null ? null : w.source === "card" ? `your answer to ${cardAnswerText(team, w.by?.[0]).split(" \u00b7 ")[0]}` : w.source === "policy" ? "your standing policy" : "your regulator";
  }
  const row = draft.activeConstraints.find((c) => subOf(c.decisionId, entry.decisionId) !== null);
  if (row === undefined) return null;
  if (row.source !== "event") return SOURCE_WORDS[row.source];
  return (row.sourceRef ?? "") === "" ? "an event answer" : `your answer to "${cardTitleOf(team, row.sourceRef)}"`;
}

const CAP_WORDS = { mou: "the regulator's agreement", consentOrder: "the regulator's order", pca: "the regulator's corrective action", dealCovenant: "the deal covenant", activist: "the activist agreement" } as const;

/**
 * "You wanted Add 20%; held at Hire with trend by the regulator's agreement": one line per setting of this decision that the round open held back and the draft still holds
 * (`DraftView.capped`), so the row says what the plan wanted, where it stands and who holds it. An event's row names the card by its title.
 */
export function cappedLines(entry: DecisionCatalogEntry, draft: DraftView, team: TeamState): readonly string[] {
  return (draft.capped ?? []).flatMap((c) => {
    const sub = subOf(c.decisionPath, entry.decisionId);
    if (sub === null) return [];
    const options = entry.options[sub] ?? [];
    const who = c.by.map((source) => {
      if (source !== "event") return CAP_WORDS[source as keyof typeof CAP_WORDS] ?? "a constraint";
      const row = draft.activeConstraints.find((k) => k.source === "event" && k.decisionId === c.decisionPath);
      return `your answer to "${cardTitleOf(team, row?.sourceRef)}"`;
    });
    return [`You wanted ${optionLabel(options, c.wanted as SettingValue)}; held at ${optionLabel(options, c.value as SettingValue)}${who.length === 0 ? "" : ` by ${who.join(" and ")}`}`];
  });
}

/** "Cost of deposits: held at Hold rates by your answer to "Deposit run"": a setting the answered cards hold in the no-change plan (`PreviewTrace.held`), so the Trace says what stayed and who holds it. */
export const heldLine = (h: NonNullable<PreviewTrace["held"]>[number], team: TeamState): string =>
  `${h.label}: held at ${h.toLabel} by ${h.by.length === 0 ? "an event answer" : `your answer to ${cardAnswerText(team, h.by[0]).split(" \u00b7 ")[0]}`}`;
